import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Search, SlidersHorizontal, X, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import JobCard from "@/components/JobCard";
import JobDrawer from "@/components/JobDrawer";
import useJobActions from "@/hooks/useJobActions";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const META = {
  job_type: ["Full-time", "Part-time", "Contract", "Internship", "Freelance"],
  work_mode: ["Remote", "Onsite", "Hybrid"],
  category: ["Engineering", "Design", "Product", "Marketing", "Data", "Sales", "Operations"],
  experience: ["Entry", "Mid", "Senior", "Lead", "Executive"],
};

const DATE_OPTS = [["any", "Any time"], ["24h", "Last 24h"], ["week", "Past week"], ["month", "Past month"]];

export default function Jobs() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ q: "", location: "", job_type: "all", work_mode: "all", category: "all", experience: "all", salary_min: 0, date_posted: "any", sort: "recent" });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const { savedIds, appliedIds, toggleSave, apply } = useJobActions();

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all" && v !== 0) params[k] = v; });
    try {
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const reset = () => setFilters({ q: "", location: "", job_type: "all", work_mode: "all", category: "all", experience: "all", salary_min: 0, date_posted: "any", sort: "recent" });

  const FilterSection = () => (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-slate-200 flex items-center gap-2"><SlidersHorizontal size={16} /> {t("jobs.filters")}</h3>
        <button onClick={reset} className="text-xs text-teal-400 hover:underline" data-testid="filters-reset">{t("jobs.reset")}</button>
      </div>

      {[["job_type", t("jobs.jobType"), "job-filter-type-select"], ["work_mode", t("jobs.workMode"), "job-filter-mode-select"], ["category", t("jobs.category"), "job-filter-category-select"], ["experience", t("jobs.experience"), "job-filter-experience-select"]].map(([key, label, tid]) => (
        <div key={key}>
          <label className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</label>
          <Select value={filters[key]} onValueChange={(v) => set(key, v)}>
            <SelectTrigger data-testid={tid} className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
            <SelectContent className="border-slate-700 text-slate-200">
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {META[key].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ))}

      <div>
        <label className="text-xs font-mono uppercase tracking-wider text-slate-500">{t("jobs.minSalary")}: <span className="text-teal-400">${Math.round(filters.salary_min / 1000)}k</span></label>
        <Slider data-testid="job-filter-salary-range" value={[filters.salary_min]} onValueChange={([v]) => set("salary_min", v)} min={0} max={250000} step={10000} className="mt-3" />
      </div>

      <div>
        <label className="text-xs font-mono uppercase tracking-wider text-slate-500">{t("jobs.datePosted")}</label>
        <Select value={filters.date_posted} onValueChange={(v) => set("date_posted", v)}>
          <SelectTrigger data-testid="job-filter-date-select" className="mt-1.5 bg-slate-950/50 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent className="border-slate-700 text-slate-200">
            {[["any", t("jobs.anyTime")], ["24h", t("jobs.last24h")], ["week", t("jobs.pastWeek")], ["month", t("jobs.pastMonth")]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <div className="mb-6">
          <h1 className="font-display font-bold text-3xl text-slate-100">{t("jobs.title")}</h1>
          <p className="text-slate-400 text-sm mt-1">{jobs.length} {t("jobs.opportunities")}</p>
        </div>

        {/* search bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="job-search-input" value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder={t("jobs.searchPh")}
              className="pl-10 bg-slate-950/50 border-slate-700 text-slate-100 h-11" />
          </div>
          <div className="relative flex-1 sm:max-w-xs">
            <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="job-location-input" value={filters.location} onChange={(e) => set("location", e.target.value)} placeholder={t("jobs.locationPh")}
              className="pl-10 bg-slate-950/50 border-slate-700 text-slate-100 h-11" />
          </div>
          <Button variant="outline" onClick={() => setShowFilters(true)} className="lg:hidden border-slate-700 text-slate-200 h-11" data-testid="mobile-filters-open">
            <SlidersHorizontal size={16} className="mr-2" /> {t("jobs.filters")}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          <aside className="hidden lg:block glass rounded-xl p-5 h-fit sticky top-24">
            <FilterSection />
          </aside>

          <div>
            <div className="flex items-center justify-between mb-4">
              <div />
              <Select value={filters.sort} onValueChange={(v) => set("sort", v)}>
                <SelectTrigger data-testid="job-sort-select" className="w-40 bg-slate-950/50 border-slate-700 text-slate-200 h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 text-slate-200">
                  <SelectItem value="recent">{t("jobs.sortRecent")}</SelectItem>
                  <SelectItem value="salary">{t("jobs.sortSalary")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-xl bg-slate-800/40" />)}
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-20 text-slate-500" data-testid="jobs-empty">{t("jobs.empty")}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {jobs.map((job) => (
                  <JobCard key={job.id} job={job} saved={savedIds.includes(job.id)} onToggleSave={toggleSave} onOpen={setSelected} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] glass border-l border-teal-500/20 p-5 overflow-y-auto">
            <button onClick={() => setShowFilters(false)} className="absolute right-4 top-4 text-slate-400"><X size={20} /></button>
            <div className="mt-6"><FilterSection /></div>
          </div>
        </div>
      )}

      <JobDrawer job={selected} open={!!selected} onClose={() => setSelected(null)}
        saved={selected && savedIds.includes(selected.id)} onToggleSave={toggleSave} onApply={apply}
        applied={selected && appliedIds.includes(selected.id)} />
    </div>
  );
}
